/**
 * PORTED — RO-3-04 / #44297.
 * Origin: `excellent/app/customers/property-facet.tsx`.
 *
 * Changes:
 *   server getCustomerPropertyFacets(workspaceId) → props from useApi
 *   next/link → RouterLink
 * Live Next original UNTOUCHED (D6).
 */

import {
  Card,
  DataTable,
  IntentBadge,
  StatStrip,
  formatUsd,
  type DataTableColumn,
} from "@excellent/ui";
import { RouterLink as Link } from "@excellent/ui/router";
import type { CustomersPropertyFacetsResponse } from "@excellent/ui/api/contracts/customers";

type PropertyFacetCustomer = CustomersPropertyFacetsResponse["properties"][number]["customers"][number];

export function CustomerPropertyFacet({
  data,
}: {
  data: CustomersPropertyFacetsResponse;
}) {
  const { noun } = data;
  const columns: DataTableColumn<PropertyFacetCustomer>[] = [
    {
      key: "name",
      header: noun.singular,
      render: (c) => (
        <Link href={`/o/${c.uid}`} className="font-medium hover:underline">
          {c.name}
        </Link>
      ),
    },
    {
      key: "stage",
      header: "Engagement",
      render: (c) =>
        c.stage === "paying" ? (
          <IntentBadge intent="success">Paying</IntentBadge>
        ) : (
          <IntentBadge intent="neutral">Touched</IntentBadge>
        ),
    },
    {
      key: "revenue",
      header: "Revenue",
      render: (c) => formatUsd(c.revenueMinor / 100),
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      <StatStrip
        items={[
          { title: "Attributed customers", value: String(data.attributedCustomers) },
          { title: "Paying", value: String(data.totalPayingCustomers) },
          { title: "Properties", value: String(data.propertyCount) },
        ]}
      />

      {data.properties.length === 0 ? (
        <Card className="p-6 type-body text-muted-foreground">
          No properties yet. A property is a top-level project — add one to start
          attributing {noun.plural.toLowerCase()} and revenue per property.
        </Card>
      ) : (
        data.properties.map((prop) => (
          <DataTable
            key={prop.slug}
            title={prop.name}
            subtitle={`${formatUsd(prop.revenueMinor / 100)} revenue · ${prop.payingCustomers} paying`}
            count={prop.customers.length}
            rows={prop.customers}
            columns={columns}
            rowKey={(c) => c.uid}
            empty={`No ${noun.plural.toLowerCase()} attributed to ${prop.name} yet.`}
          />
        ))
      )}

      <p className="px-1 type-subtext text-muted-foreground">
        Per-property engagement from <code>customerPropertyEngagement</code> over the
        canonical base parties (#1192 — the same set the portfolio home rolls up).
        One business, one line — properties are a drill dimension, never a flip.
      </p>
    </div>
  );
}
